import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ShieldCheck, HandHeart } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function AdminLogin() {
    const navigate = useNavigate();
    const { user, login } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (user && user !== false) {
            navigate("/admin", { replace: true });
        }
    }, [user, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (!email.trim() || !password) {
            setError("Email and password are required.");
            return;
        }
        setSubmitting(true);
        const result = await login(email.trim().toLowerCase(), password);
        setSubmitting(false);
        if (result.ok) {
            toast.success("Welcome back.");
            navigate("/admin", { replace: true });
        } else {
            setError(result.error);
        }
    };

    const inputClass =
        "border border-[#E5E7EB] rounded-md px-4 py-3 bg-white focus:border-[#0033CC] focus:ring-1 focus:ring-[#0033CC] focus:outline-none w-full transition-colors text-[#0033CC] placeholder:text-[#9CA3AF]";
    const labelClass = "block text-sm font-medium text-[#0033CC] mb-1.5";

    return (
        <div className="min-h-screen bg-[#F5F3F0] flex items-center justify-center px-4 py-12 font-body text-[#0033CC]">
            <div className="w-full max-w-md">
                <Link
                    to="/"
                    data-testid="login-back-home"
                    className="flex items-center gap-2 font-heading font-bold text-[#0033CC] mb-8"
                >
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-[#0033CC] text-white">
                        <HandHeart className="h-5 w-5" />
                    </span>
                    <span className="leading-tight">
                        Neighborhood
                        <span className="block text-xs font-medium text-[#4B5563] tracking-wide">
                            Supply Partners
                        </span>
                    </span>
                </Link>

                <div className="bg-white border border-[#E5E7EB] rounded-md p-8 shadow-sm">
                    <div className="flex items-center gap-2 text-[#0033CC]/70">
                        <ShieldCheck className="h-4 w-4" />
                        <span className="text-xs font-semibold uppercase tracking-[0.18em]">
                            Admin sign-in
                        </span>
                    </div>
                    <h1 className="mt-3 font-heading text-3xl font-bold text-[#0033CC]">
                        Review incoming inquiries.
                    </h1>
                    <p className="mt-2 text-sm text-[#4B5563]">
                        Sign in to manage contact submissions from schools, families, and
                        community partners.
                    </p>

                    <form onSubmit={handleSubmit} className="mt-7 space-y-5" data-testid="admin-login-form" noValidate>
                        <div>
                            <label htmlFor="admin-email" className={labelClass}>
                                Email
                            </label>
                            <input
                                id="admin-email"
                                type="email"
                                autoComplete="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="admin@neighborhoodsupplypartners.com"
                                className={inputClass}
                                data-testid="admin-login-email"
                                required
                            />
                        </div>
                        <div>
                            <label htmlFor="admin-password" className={labelClass}>
                                Password
                            </label>
                            <input
                                id="admin-password"
                                type="password"
                                autoComplete="current-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                className={inputClass}
                                data-testid="admin-login-password"
                                required
                            />
                        </div>

                        {error && (
                            <p
                                data-testid="admin-login-error"
                                className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2"
                            >
                                {error}
                            </p>
                        )}

                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="admin-login-submit"
                            className="w-full bg-[#0033CC] text-white hover:bg-[#1A52E0] transition-colors rounded-md px-6 py-3 font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {submitting ? "Signing in…" : "Sign in"}
                            {!submitting && <ArrowRight className="h-4 w-4" />}
                        </button>
                    </form>
                </div>

                <p className="mt-6 text-center text-xs text-[#6B7280]">
                    Admin credentials are managed in your backend environment file.
                </p>
            </div>
        </div>
    );
}
